import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UploadSimple, Trash, Star } from "@phosphor-icons/react";
import { toast } from "sonner";

export const loginPhotoSrc = (url) => (url?.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

function PhotoTile({ photo, onDelete }) {
  return (
    <div className="group relative rounded-2xl overflow-hidden border border-line bg-muted2 aspect-[2/3]" data-testid={`login-photo-${photo.id}`}>
      <img src={loginPhotoSrc(photo.url)} alt={photo.label || "Sign-in photo"} className="h-full w-full object-cover" loading="lazy" />
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-ink/80 to-transparent text-white">
        <div className="text-sm font-semibold truncate">{photo.label || "Photo"}</div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-white/70">{photo.kind === "starter" ? "Starter" : "Your upload"}</div>
      </div>
      {photo.is_current && (
        <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-terracotta text-white text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-1"
              data-testid={`login-photo-current-${photo.id}`}>
          <Star size={10} weight="fill" /> This week
        </span>
      )}
      <button type="button" onClick={() => onDelete(photo)} aria-label="Remove photo"
        className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-white/90 text-error hover:bg-white transition-colors"
        data-testid={`login-photo-delete-${photo.id}`}>
        <Trash size={14} />
      </button>
    </div>
  );
}

export default function LoginPhotos() {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try { setPhotos((await api.get("/login-photos")).data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try { await api.post("/login-photos", fd, { headers: { "Content-Type": "multipart/form-data" } }); ok += 1; }
      catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
    if (ok) toast.success(`${ok} photo${ok === 1 ? "" : "s"} added to the rotation`);
    load();
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/login-photos/${pending.id}`);
      toast.success("Photo removed from the rotation");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setPending(null);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="login-photos-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="hs-label">Sign-in page</div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-ink mt-1">Sign-in photos</h1>
          <p className="text-sm text-ink-2 mt-2 max-w-xl">
            One photo shows on the sign-in page each week, rotating through everything below every Monday.
            Upload real photos of your gym and athletes — tall (portrait) shots look best.
          </p>
        </div>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
               onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="login-photo-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={busy}
                className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white" data-testid="login-photo-upload-button">
          <UploadSimple size={16} className="mr-2" /> {busy ? "Uploading…" : "Upload photos"}
        </Button>
      </div>

      {photos.length === 0 ? (
        <p className="mt-10 text-sm text-ink-3" data-testid="login-photos-empty">
          No photos in the rotation — the default couple photo will show until you upload one.
        </p>
      ) : (
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p) => <PhotoTile key={p.id} photo={p} onDelete={setPending} />)}
        </div>
      )}

      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove this photo?</AlertDialogTitle>
            <AlertDialogDescription>It will no longer appear on the sign-in page.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="login-photo-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-terracotta hover:bg-terracotta-hover text-white"
                               data-testid="login-photo-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
